import { CircleCheck } from 'lucide-react';

import { useElapsedSeconds } from './SignalsStatus';

/**
 * The checklist above the waiting line: the steps this answer takes, ticked as
 * the server really finishes each one.
 *
 * The server owns the list (signals-copilot server/answer_progress.py) and
 * sends it on the status message as metadata.signals_steps. Nothing here
 * guesses or times anything: a step is done because the server said so, and a
 * step that only some answers have (the user's choice, a report from the skill
 * form) appears only in the answers that have it. That is also why a tab that
 * opens the thread mid-answer sees the same ticks as the one that asked.
 *
 * Hidden for the first two seconds of the wait, so a quick answer does not
 * flash a list on its way to the text. English always: the system talking.
 */

export type StepState = 'pending' | 'active' | 'done';

export interface AnswerStep {
  id: string;
  label: string;
  state: StepState;
  detail?: string;
}

const STATES: StepState[] = ['pending', 'active', 'done'];
const SHOW_AFTER_SECONDS = 2;

/** The steps in a message's metadata, or [] when there are none worth drawing. */
export function stepsOf(
  metadata: Record<string, any> | undefined
): AnswerStep[] {
  const raw = metadata?.signals_steps;
  if (!Array.isArray(raw)) return [];
  const steps: AnswerStep[] = [];
  for (const s of raw) {
    if (!s || typeof s !== 'object') continue;
    if (typeof s.id !== 'string' || typeof s.label !== 'string' || !s.label) {
      continue;
    }
    if (!STATES.includes(s.state)) continue;
    steps.push({
      id: s.id,
      label: s.label,
      state: s.state,
      detail:
        typeof s.detail === 'string' && s.detail.trim()
          ? s.detail.trim()
          : undefined
    });
  }
  return steps;
}

/** When the wait began, as the server stamped it. */
export function startedAtOf(
  metadata: Record<string, any> | undefined
): number | undefined {
  const at = metadata?.signals_started_at;
  return typeof at === 'number' && Number.isFinite(at) ? at : undefined;
}

const StepIcon = ({ state }: { state: StepState }) => {
  if (state === 'done') {
    return (
      <CircleCheck
        size={16}
        strokeWidth={2}
        style={{ color: 'var(--sig-positive)' }}
        aria-hidden="true"
      />
    );
  }
  if (state === 'active') {
    return <span className="sig-step-active" aria-hidden="true" />;
  }
  return <span className="sig-step-pending" aria-hidden="true" />;
};

interface Props {
  steps: AnswerStep[];
  startedAt?: number;
}

export const AnswerProgress = ({ steps, startedAt }: Props) => {
  const elapsed = useElapsedSeconds(startedAt);
  if (!steps.length || elapsed < SHOW_AFTER_SECONDS) return null;
  return (
    <ul
      className="sig-steps flex flex-col gap-1.5 mb-1"
      aria-label="Progress"
      data-testid="answer-progress"
    >
      {steps.map((step) => (
        <li
          key={step.id}
          className="flex items-start gap-2.5 text-[13.5px] leading-5"
          data-state={step.state}
        >
          <span className="w-4 h-5 flex items-center justify-center shrink-0">
            <StepIcon state={step.state} />
          </span>
          <span className="flex flex-col min-w-0">
            <span
              style={{
                color:
                  step.state === 'active'
                    ? 'var(--sig-ink)'
                    : step.state === 'done'
                      ? 'var(--sig-muted)'
                      : 'var(--sig-subtle)'
              }}
            >
              {step.label}
              <span className="sr-only">
                {step.state === 'done'
                  ? ' (done)'
                  : step.state === 'active'
                    ? ' (in progress)'
                    : ''}
              </span>
            </span>
            {step.detail ? (
              <span
                className="text-[12.5px] leading-[18px]"
                style={{ color: 'var(--sig-subtle)' }}
                dir="auto"
              >
                {step.detail}
              </span>
            ) : null}
          </span>
        </li>
      ))}
    </ul>
  );
};
